import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Table, TableBody,
    TableCell, TableHead, TableRow, TextField,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { schoolApiSlice, School, SchoolAdmin } from '../../../features/school/schoolSlice'
import { courseApiSlice } from '../../../features/course/courseSlice'
import ConfirmActionDialog from '../../../components/dialogs/ConfirmActionDialog'
import { ui } from '../../../utils/uiTokens'
import { titleOf } from '../titleOf'

interface Props {
    school: School
    onClose: () => void
}

// Админы школы: создать по телефону (выдаётся временный пароль) и убрать
const SchoolAdminsDialog: React.FC<Props> = ({ school, onClose }) => {
    const { formatMessage, locale } = useIntl()
    const [form, setForm] = useState({ firstName: '', lastName: '', mobile: '' })
    const [toRemove, setToRemove] = useState<SchoolAdmin | null>(null)

    const { data: admins = [] } = schoolApiSlice.useGetSchoolAdminsQuery(school.id)
    const { data: schoolCourse } = schoolApiSlice.useGetSchoolCourseQuery(school.id)
    const { data: courses = [] } = courseApiSlice.useFetchAllCoursesQuery()
    const [setSchoolCourse, { isLoading: savingCourse }] = schoolApiSlice.useSetSchoolCourseMutation()

    // Курс, на который зачисляются ученики школы: предметы они выбирают сами при первом входе
    const handleCourse = async (courseId: number) => {
        try {
            await setSchoolCourse({ schoolId: school.id, courseId }).unwrap()
            toast.success(formatMessage({ id: 'schoolCourseSaved' }))
        } catch { /* общий обработчик */ }
    }
    const [createAdmin, { isLoading: creating }] = schoolApiSlice.useCreateSchoolAdminMutation()
    const [removeAdmin, { isLoading: removing }] = schoolApiSlice.useRemoveSchoolAdminMutation()

    const canCreate = form.firstName.trim() && form.lastName.trim()

    const handleCreate = async () => {
        try {
            await createAdmin({ schoolId: school.id, ...form }).unwrap()
            setForm({ firstName: '', lastName: '', mobile: '' })
            toast.success(formatMessage({ id: 'schoolAdminCreated' }))
        } catch { /* общий обработчик покажет ошибку */ }
    }

    const handleRemove = async () => {
        if (!toRemove) return
        try {
            await removeAdmin({ schoolId: school.id, userId: toRemove.userId }).unwrap()
            setToRemove(null)
        } catch { /* общий обработчик */ }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {formatMessage({ id: 'schoolAdmins' })}: {titleOf(school, locale)}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                    <TextField
                        select
                        size="small"
                        label={formatMessage({ id: 'schoolCourse' })}
                        value={schoolCourse?.courseId ?? 0}
                        disabled={savingCourse}
                        onChange={(e) => handleCourse(Number(e.target.value))}
                        sx={{ minWidth: 320 }}
                    >
                        <MenuItem value={0}>{formatMessage({ id: 'schoolNoCourse' })}</MenuItem>
                        {courses.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{titleOf(c, locale)}</MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ fontSize: '12.5px', color: ui.sub, maxWidth: 380 }}>{formatMessage({ id: 'schoolCourseHint' })}</Box>
                </Box>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{formatMessage({ id: 'fullName' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'schoolLogin' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'phone' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'temporaryPassword' })}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {admins.map((a) => (
                            <TableRow key={a.userId}>
                                <TableCell>{a.lastName} {a.firstName}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{a.username ?? a.mobile ?? '-'}</TableCell>
                                <TableCell>{a.mobile ?? '-'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {a.tempPassword ?? <Box component="span" sx={{ color: ui.muted }}>{formatMessage({ id: 'schoolPasswordChanged' })}</Box>}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => setToRemove(a)}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {admins.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ color: ui.muted }}>
                                    {formatMessage({ id: 'schoolNoAdmins' })}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <Box sx={{ mt: 3, fontWeight: 700, color: ui.ink }}>{formatMessage({ id: 'schoolAdminAdd' })}</Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    <TextField size="small" label={formatMessage({ id: 'lastName' })} value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                    <TextField size="small" label={formatMessage({ id: 'firstName' })} value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                    <TextField size="small" label={`${formatMessage({ id: 'phone' })} (${formatMessage({ id: 'schoolOptional' })})`} value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                    <Button variant="contained" disabled={!canCreate || creating} onClick={handleCreate}>
                        {formatMessage({ id: 'add' })}
                    </Button>
                </Box>
                <Box sx={{ mt: 1, fontSize: '12.5px', color: ui.sub }}>{formatMessage({ id: 'schoolAdminHint' })}</Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{formatMessage({ id: 'close' })}</Button>
            </DialogActions>

            <ConfirmActionDialog
                open={!!toRemove}
                setOpen={(open) => !open && setToRemove(null)}
                confirmAction={handleRemove}
                loading={removing}
                contentText={formatMessage({ id: 'schoolAdminRemoveConfirm' })}
            />
        </Dialog>
    )
}

export default SchoolAdminsDialog
